import { axiosInstance } from '../App';

import { AdministrativeFormStateTypeI18n, EVatCustomerCategory, PaymentCondition, PaymentMethod, SupplierTypeDTO } from '../interface/interface';
import { GetItemsProps } from 'medere-ui-framework';
import { SupplierForm } from '../views/Supplier/SupplierModal';
import { Movement } from '../views/Movements';
import { ComboDataDTO } from '../dto/ComboDataDTO';

export function getCities({ filterValue, offSet, pageSize }: GetItemsProps) {
  return axiosInstance.post(`/api/public/site/getCities`, null, {
    params: {
      inputText: filterValue ?? '',
      offSet: offSet,
      pageSize: pageSize,
    }
  }).then(response => {
    return response.data
  })
}

export function getAllSuppliers(offSet: number, pageSize: number, params: any) {
  return axiosInstance.post(`/api/secured/supplier/getAllSuppliers`, {
    loadConfigDTO: {
      offset: offSet,
      pageSize: pageSize,
      sortingFieldInfoList: params.multiSortMeta,
      filters: params.filterDTO,
    },
    supplierStatus: params.isActive,
  }, {
    params: {
    }
  }).then(response => response.data)
}

export function getAllSuppliersCombo({ filterValue, offSet, pageSize }: GetItemsProps) {
  return axiosInstance.get(`/api/secured/supplier/getAllSuppliers`, {
    params: {
      filterValue: filterValue ?? '',
      offSet,
      pageSize,
    }
  }).then(response => response.data)
}

export function removeSupplier(selectedSuppliers: any) {
  return axiosInstance.delete(`/api/secured/supplier/deleteSupplier`, {
    params: {
      medereEntity: selectedSuppliers.medereentity
    }
  })
}

export function getCreditAndDebit(inputText: any, offset: any, pageSize: any) {
  return axiosInstance.get(`/api/secured/supplier/getCreditAndDebit`, {
    params: {
      inputText,
      offset,
      pageSize,
    }
  }).then(response => response.data)
}

export function getVatCustomerCategories({ filterValue, offSet, pageSize }: GetItemsProps) {
  return axiosInstance.get(`/api/secured/supplier/getVatCustomerCategories`, {
    params: {
      inputText: filterValue ?? '',
      offSet,
      pageSize,
    }
  }).then((response) => response.data)
}

export function getTaxTypes() {
  return axiosInstance.get(`/api/secured/supplier/getTaxTypes`, {
  }).then(response => response.data)
}

export function getAllAdministrativeFormTypes({ filterValue, offSet, pageSize }: GetItemsProps) {
  return axiosInstance.get(`/api/secured/supplier/getAllAdministrativeTypes`, {
    params: {
      inputText: filterValue,
      offset: offSet,
      pageSize,
    }
  }).then((response) => response.data)
}

export function loadAllPOS({ filterValue, offSet, pageSize, params }: GetItemsProps) {
  return axiosInstance.get(`/api/secured/supplier/loadAllPOS`, {
    params: {
      inputText: filterValue,
      offset: offSet,
      pageSize,
      adminformtype: params.adminformtype,
    }
  }).then((response) => response.data)
}

export function getCompanyEntities(inputText: string, offset: number, pageSize: number, params: any) {
  return axiosInstance.post(`/api/secured/profile/profiledataform/getCompanyEntities`, null, {
    params: {
      filter: inputText,
      offset,
      limit: pageSize,
      medereentitytag: params.medereentitytag,
    }
  }).then((response) => response.data)
}

export function saveSupplier(generalForm: SupplierForm) {
  const { businessName, commercialName, phone, secondaryPhone, email, city, postalCode, active, invoiceAddress, fiscalDocument, documentType, observations, condIva, paymentMethod, paymentCondition, supplierType, exempt, exemptDate, daysForExpiration, earningWithHoldingConcept } = generalForm
  return axiosInstance.post(`/api/secured/supplier`, {
    businessname: businessName,
    commercialname: commercialName,
    phone,
    secondaryphone: secondaryPhone,
    email,
    city: city?.id,
    cityExternalId: city?.isExternalId ?? false,
    cityname: city?.name,
    postalcode: postalCode,
    active: active,
    address: invoiceAddress,
    daysforexpiration: daysForExpiration,
    document: fiscalDocument,
    observations: observations,
    vatidtype: documentType?.documentType,
    vatid: fiscalDocument,
    vatcategory: condIva?.vatcustomercategory,
    invoicebusinessname: businessName,
    invoiceaddress: invoiceAddress,
    // La imputación contable se configura posteriormente y no forma parte del ABM básico.
    credit: null,
    debit: null,
    paymentmethod: paymentMethod?.paymentMethod,
    paymentcondition: paymentCondition?.paymentcondition,
    exempt,
    totime: exemptDate,
    suppliertype: supplierType?.suppliertype,
    earningwithholdingconcept: condIva?.vatcustomercategory !== EVatCustomerCategory.MONOTRIBUTISTA ? earningWithHoldingConcept?.withholdingconcept : null,
  })
}

export function updateSupplier(generalForm: SupplierForm) {
  const { businessName, commercialName, phone, secondaryPhone, email, city, postalCode, active, invoiceAddress, fiscalDocument, documentType, observations, condIva, paymentMethod, paymentCondition, supplierType, exempt, exemptDate, daysForExpiration, medereentity, earningWithHoldingConcept } = generalForm
  return axiosInstance.put(`/api/secured/supplier`, {
    medereentity: medereentity,
    businessname: businessName,
    commercialname: commercialName,
    phone,
    secondaryphone: secondaryPhone,
    email,
    city: city?.id,
    cityExternalId: city?.isExternalId ?? false,
    cityname: city?.name,
    postalcode: postalCode,
    active,
    address: invoiceAddress,
    daysforexpiration: daysForExpiration,
    document: fiscalDocument,
    observations,
    vatidtype: documentType?.documentType,
    vatid: fiscalDocument,
    vatcategory: condIva?.vatcustomercategory,
    invoicebusinessname: businessName,
    invoiceaddress: invoiceAddress,
    // La imputación contable se configura posteriormente y no forma parte del ABM básico.
    credit: null,
    debit: null,
    paymentmethod: paymentMethod?.paymentMethod,
    paymentcondition: paymentCondition?.paymentcondition,
    exempt,
    totime: exemptDate,
    suppliertype: supplierType?.suppliertype,
    earningwithholdingconcept: condIva?.vatcustomercategory !== EVatCustomerCategory.MONOTRIBUTISTA ? earningWithHoldingConcept?.withholdingconcept : null,
  })
}

export function getPaymentMethods(
  { filterValue }: GetItemsProps): Promise<PaymentMethod[]> {
  return axiosInstance.get(`/api/secured/supplier/getPaymentMethods`, {
    params: {
      inputText: filterValue ?? '',
    }
  }).then((response) => response.data)
}

export function getPaymentMethodsForCombo(
  { filterValue }: GetItemsProps): Promise<ComboDataDTO[]> {
  return axiosInstance.get(`/api/secured/supplier/getPaymentMethods`, {
    params: {
      inputText: filterValue ?? '',
    }
  }).then((response) => {
    return response.data.map((pm: PaymentMethod) => ({
      id: pm.paymentMethod,
      name: pm.methodName
    }));
  })
}

export function getPaymentConditions({ filterValue, offSet, pageSize }: GetItemsProps): Promise<PaymentCondition[]> {
  return axiosInstance.get(`/api/secured/supplier/getPaymentConditions`, {
    params: {
      inputText: filterValue ?? '',
      pageSize,
      offSet,
    }
  }).then((response) => response.data)
}

export function getSupplierType({ filterValue, offSet, pageSize }: GetItemsProps): Promise<SupplierTypeDTO[]> {
  return axiosInstance.get(`/api/secured/supplier/getSupplierTypes`, {
    params: {
      inputText: filterValue ?? '',
      pageSize,
      offSet,
    }
  }).then((response) => response.data)
}

export function getEarningWithHoldings(
  { filterValue, offSet, pageSize }: GetItemsProps) {
  return axiosInstance.get(`/api/secured/supplier/getEarningWithHoldings`, {
    params: {
      inputText: filterValue ?? '',
      pageSize,
      offSet,
    }
  }).then((response) => response.data)
}

export function saveIIBB({ percentage, fromTime, toTime }: { percentage: number, fromTime: any, toTime: any }) {
  return axiosInstance.get(`/api/secured/supplier/saveArbaIIBB`, {
    params: {
      percentage,
      fromTime,
      toTime,
    }
  }).then((response) => response.data)
}

export function getArbaCensus({ cuit }: { cuit: string }) {
  return axiosInstance.get(`/api/secured/supplier/getArbaCensus`, {
    params: {
      cuit
    }
  }).then((response) => response.data)
}

export function generateAutomaticAccountingMovement({ administrativeFormId }: { administrativeFormId: number }) {
  return axiosInstance.get(`/api/secured/supplier/generateAutomaticAccountingMovement`, {
    params: {
      administrativeFormId,
    }
  }).then((resp) => resp.data)
}

export function getAfipStatusByVatCustomerCategory({ offSet, pageSize, params, filterValue }: GetItemsProps<{ vatCustomerCategory?: number }>) {

  return axiosInstance.get('/api/secured/supplier/getAfipStatusByVatCustomerCategory', {
    params: {
      offSet,
      pageSize,
      inputtext: filterValue,
      vatCustomerCategory: params?.vatCustomerCategory
    }
  }).then(res => res.data)
}

export function getMovements(offSet: number, pageSize: number, params: any): Promise<Movement[]> {
  return axiosInstance.post(`/api/secured/supplier/getAllMovements`, {
    loadConfigDTO: {
      offSet,
      pageSize,
      sortingFieldInfoList: params.multiSortMeta,
      filters: params.filterDTO,
    },
    movementStatus: params.movementStatus,
  }, {
    params: {
    }
  }).then(response => response.data)
}

export function getMovementsCombo({ offSet, pageSize, params, filterValue }: GetItemsProps): Promise<Movement[]> {
  return axiosInstance.get(`/api/secured/supplier/getAllMovements`, {
    params: {
      inputText: filterValue ?? '',
      offSet,
      pageSize,
      supplier: params.supplier,
    }
  }).then(response => response.data)
}

export function getMovementStatus({ filterValue, offSet, pageSize }: GetItemsProps): Promise<AdministrativeFormStateTypeI18n[]> {
  return axiosInstance.get(`/api/secured/supplier/getMovementStatus`, {
    params: {
      filterValue: filterValue ?? '',
      offSet,
      pageSize,
    }
  }).then(response => response.data)
}

export function getAllSuppliersWithBalance({ filterValue, offSet, pageSize }: GetItemsProps) {
  return axiosInstance.get(`/api/secured/supplier/getAllSuppliersWithBalance`, {
    params: {
      inputText: filterValue ?? '',
      offSet,
      pageSize,
    }
  }).then(resp => resp.data)
    .catch(error => {
      if (error.response) {
        console.error("Status:", error.response.status, "Data:", error.response.data);
      }
      throw error;
    });
}
